import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, FormControl, Stack } from '@mui/material';

import { DeleteActionIconButton } from '@/components/actions';
import { CustomFormLabel, RHFIntegerTextField } from '@/components/form-controls';
import { ReactComponent as AddSVG } from '@/shared/assets/icons-2.0/add.svg';

type BarcodeLabelProps = {
  barcodeIndex: number;
};

const BarcodeLabel = (props: BarcodeLabelProps) => {
  const { barcodeIndex } = props;
  const { t } = useTranslation('nomenclature-page');

  const barcodeNumber = barcodeIndex !== 0 ? ` №${barcodeIndex + 1}` : '';

  return (
    <CustomFormLabel
      htmlFor={`barcodes.${barcodeIndex}.value`}
    >{`${t('modals.form.barcode')}${barcodeNumber}`}</CustomFormLabel>
  );
};

export const NomenclatureFormBarcodes = () => {
  const { control } = useFormContext();
  const { t } = useTranslation('nomenclature-page');
  const {
    fields: barcodes,
    append,
    remove,
  } = useFieldArray({
    rules: { required: true },
    control,
    name: 'barcodes',
  });

  const handleAppendBarcode = () => append({ value: '' });
  const handleRemoveBarcode = (index: number) => remove(index);

  return (
    <Stack spacing={2}>
      <Stack component="ul" spacing={2}>
        {barcodes.map((barcode, index) => (
          <Stack direction="row" spacing={2} alignItems="center" component="li" key={barcode.id}>
            <FormControl fullWidth>
              <BarcodeLabel barcodeIndex={index} />
              <RHFIntegerTextField
                allowLeadingZeros
                fullWidth
                name={`barcodes.${index}.value`}
                id={`barcodes.${index}.value`}
                variant="filled"
                thousandSeparator=""
                allowNegative={false}
              />
            </FormControl>
            {barcodes.length > 1 && (
              <DeleteActionIconButton sx={{ marginTop: 2 }} onClick={() => handleRemoveBarcode(index)} />
            )}
          </Stack>
        ))}
      </Stack>
      <Button
        sx={{ maxWidth: '210px' }}
        variant="outlined"
        color="secondary"
        startIcon={<AddSVG fontSize="medium" />}
        onClick={handleAppendBarcode}
      >
        {t('modals.form.add-barcode')}
      </Button>
    </Stack>
  );
};
